import React, { useState } from 'react';
import axios from '../config/axiosConfig';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../hooks/useToast';
import Toast from './Toast';
import FeedbackForm from './customer/FeedbackForm';
import BillPreviewModal from './BillPreviewModal';

const OrderBillSender = ({ orderId, order, showFeedback = true }) => {
    const { toasts, showToast, removeToast } = useToast();
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [showBillPreview, setShowBillPreview] = useState(false);
    const [email, setEmail] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [loadingBill, setLoadingBill] = useState(false);

    // Update billOrder when order prop changes
    // useEffect(() => {
    //     if (order) {
    //         setBillOrder(order);
    //     }
    // }, [order]);

    const sendBill = async (via) => {
        try {
            setIsSending(true);
            const response = await axios.get(API_ENDPOINTS.ORDER_SEND_BILL(orderId, via));
            showToast(`Bill sent successfully via ${via}!`, 'success');
        } catch (error) {
            console.error('Error sending bill:', error);
            showToast('Failed to send bill. Please try again.', 'error');
        } finally {
            setIsSending(false);
        }
    };

    const sendBillViaEmail = async () => {
        if (!email || !email.includes('@')) {
            showToast('Please enter a valid email address', 'error');
            return;
        }
        try {
            setIsSending(true);
            const response = await axios.get(API_ENDPOINTS.ORDER_SEND_BILL_EMAIL(orderId, email));
            showToast('Bill sent successfully via email!', 'success');
            setShowEmailModal(false);
            setEmail('');
            // Show feedback form after bill is sent only for customer context
            if (showFeedback) {
                setTimeout(() => {
                    setShowFeedbackForm(true);
                }, 500);
            }
        } catch (error) {
            console.error('Error sending bill via email:', error);
            showToast('Failed to send bill via email. Please try again.', 'error');
        } finally {
            setIsSending(false);
        }
    };

    const handleGenerateBill = () => {
        if (showFeedback) {
            setShowFeedbackForm(true);
        } else {
            // Restaurant context: show bill preview (view/print)
            if (order) {
                setShowBillPreview(true);
            } else {
                showToast('Order details are not available for the bill.', 'error');
            }
        }
    };

    return (
        <>
            <div className="mt-2 flex flex-wrap gap-2">
                {/* <button
                    onClick={() => sendBill('sms')}
                    disabled={isSending}
                    className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-3 py-1 rounded transition-colors duration-200 text-sm font-medium"
                >
                    📱 Send Bill (SMS)
                </button>
                <button
                    onClick={() => sendBill('whatsapp')}
                    disabled={isSending}
                    className="bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white px-3 py-1 rounded transition-colors duration-200 text-sm font-medium"
                >
                    💬 Send Bill (WhatsApp)
                </button> */}
                <button
                    onClick={() => setShowEmailModal(true)}
                    disabled={isSending}
                    className="bg-purple-500 hover:bg-purple-600 disabled:bg-purple-300 text-white px-3 py-1 rounded transition-colors duration-200 text-sm font-medium"
                >
                    📧 Send Bill (Email)
                </button>
                <button
                    onClick={handleGenerateBill}
                    className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded transition-colors duration-200 text-sm font-medium"
                >
                    📄 Generate Bill
                </button>
            </div>

            {/* Email Modal */}
            {showEmailModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                        <h3 className="text-xl font-bold mb-4 text-gray-800">Send Bill via Email</h3>
                        <p className="text-gray-600 mb-4 text-sm">Enter the customer's email address to send the bill as a PDF attachment.</p>
                        <input
                            type="email"
                            placeholder="customer@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-4"
                            onKeyPress={(e) => e.key === 'Enter' && sendBillViaEmail()}
                        />
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => {
                                    setShowEmailModal(false);
                                    setEmail('');
                                }}
                                disabled={isSending}
                                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-800 rounded-lg transition-colors duration-200 font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={sendBillViaEmail}
                                disabled={isSending}
                                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-purple-300 text-white rounded-lg transition-colors duration-200 font-medium"
                            >
                                {isSending ? 'Sending...' : 'Send Email'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!showFeedback && order && (
                <BillPreviewModal
                    isOpen={showBillPreview}
                    onClose={() => setShowBillPreview(false)}
                    order={order}
                    orderId={orderId}
                    tryServerPrint
                    onNotify={showToast}
                />
            )}

            {/* Feedback Form (only for customer context) */}
            {showFeedback && showFeedbackForm && (
                <FeedbackForm
                    orderId={orderId}
                    order={order}
                    onClose={() => setShowFeedbackForm(false)}
                    onSubmitted={() => {
                        // Optionally update order status to CLOSED
                        if (orderId) {
                            axios.patch(API_ENDPOINTS.ORDER_UPDATE_STATUS(orderId), { status: 'CLOSED' })
                                .catch(err => console.error('Failed to update order status:', err));
                        }
                    }}
                />
            )}

            {/* Toast Notifications */}
            {toasts.map(toast => (
                <Toast
                    key={toast.id}
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={() => removeToast(toast.id)}
                />
            ))}
        </>
    );
};

export default OrderBillSender;
